import React from "react";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import { RootDispatch, RootState } from "../../../store";
import {
  fetchVendorsThunk,
  createVendorThunk,
  updateVendorThunk,
} from "../../../store/slices/warehouseSlice";
import { IVendor } from "../../../models/vendor.model";

interface VendorModalProps {
  isOpen: boolean;
  onClose: () => void;
  editMode: boolean;
  selectedItem: IVendor | null;
  vendorForm: Partial<IVendor>;
  setVendorForm: React.Dispatch<React.SetStateAction<Partial<IVendor>>>;
  vendorPage: number;
  itemsPerPage: number;
  searchQuery: string;
}

const Button: React.FC<{
  type: string;
  onClick: (e?: React.FormEvent) => void | Promise<void>;
  className: string;
  disabled?: boolean;
  children: React.ReactNode;
}> = ({ type, onClick, className, disabled = false, children }) => (
  <button
    type={type as "button" | "submit"}
    onClick={onClick}
    disabled={disabled}
    className={`px-4 py-2 rounded-md font-medium transition-colors flex items-center justify-center min-w-[100px]
      ${disabled ? "cursor-not-allowed opacity-70" : ""}
      ${className}`}
  >
    {children}
  </button>
);

const VendorModal: React.FC<VendorModalProps> = ({
  isOpen,
  onClose,
  editMode,
  selectedItem,
  vendorForm,
  setVendorForm,
  vendorPage,
  itemsPerPage,
  searchQuery,
}) => {
  const dispatch = useDispatch<RootDispatch>();
  const { loading } = useSelector((state: RootState) => state.warehouse);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setVendorForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    try {
      if (
        !vendorForm.name ||
        !vendorForm.contactNumber ||
        !vendorForm.address
      ) {
        toast.error("Please fill all required fields.");
        return;
      }
      if (editMode && selectedItem) {
        await dispatch(
          updateVendorThunk({
            id: selectedItem._id,
            data: vendorForm,
          }),
        ).unwrap();
      } else {
        await dispatch(
          createVendorThunk(
            vendorForm as {
              name: string;
              gstNumber: string;
              contactNumber: string;
              address: string;
            },
          ),
        ).unwrap();
      }
      await dispatch(
        fetchVendorsThunk({
          page: vendorPage,
          limit: itemsPerPage,
          search: searchQuery,
        }),
      );
      onClose();
    } catch (err: any) {
      toast.error(
        err?.message || `Failed to ${editMode ? "update" : "create"} vendor`,
      );
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[10000] animate-fade-in">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden animate-scale-in">
        <div className="bg-gray-100 px-6 py-4 flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-800">
            {editMode ? "Edit" : "Add"} Vendor
          </h2>
          <button
            onClick={onClose}
            className="text-gray-600 hover:text-gray-800 focus:outline-none"
            aria-label="Close modal"
          >
            <svg
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid grid-cols-1 gap-5">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={vendorForm.name || ""}
                  onChange={handleChange}
                  className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  GST Number *
                </label>
                <input
                  type="text"
                  name="gstNumber"
                  value={vendorForm.gstNumber || ""}
                  onChange={handleChange}
                  className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Contact Number *
                </label>
                <input
                  type="text"
                  name="contactNumber"
                  value={vendorForm.contactNumber || ""}
                  onChange={handleChange}
                  className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Address *
                </label>
                <input
                  type="text"
                  name="address"
                  value={vendorForm.address || ""}
                  onChange={handleChange}
                  className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  required
                />
              </div>
            </div>
            <div className="flex justify-end space-x-3 pt-4">
              <Button
                type="submit"
                onClick={handleSubmit}
                disabled={loading}
                className={`bg-[#0077C0] text-white ${
                  loading ? "hover:bg-[#0077C0]" : "hover:bg-[#005B90]"
                }`}
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                ) : editMode ? (
                  "Update"
                ) : (
                  "Create"
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default VendorModal;
